import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    app_title: 'Truth Church',
    app_subtitle: 'Security System',
    primary_color: '#3b82f6',
    logo_url: '',
    font_heading: 'Chivo',
    font_body: 'Inter'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublicSettings();
  }, []);

  // Apply CSS variables when settings change
  useEffect(() => {
    applyTheme(settings);
  }, [settings]);

  const fetchPublicSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/public`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch public settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (s) => {
    const root = document.documentElement;
    
    // Apply primary color
    if (s.primary_color) {
      root.style.setProperty('--primary-custom', s.primary_color);
      // Also set as CSS variable for components
      root.style.setProperty('--brand-primary', s.primary_color);
    }
    
    // Apply fonts
    if (s.font_heading) {
      root.style.setProperty('--font-heading', s.font_heading);
    }
    if (s.font_body) {
      root.style.setProperty('--font-body', s.font_body);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const refreshSettings = () => {
    fetchPublicSettings();
  };

  const getLogoUrl = () => {
    if (settings.logo_url && settings.logo_url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`;
    }
    return settings.logo_url;
  };

  const value = {
    settings,
    loading,
    updateSettings,
    refreshSettings,
    getLogoUrl
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
